import React, { useEffect, useState } from 'react';
import { Link, router, usePage, useForm } from '@inertiajs/react';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { formatDistanceToNow, parseISO } from 'date-fns';
import axios from 'axios';
import CreditsInfo from '../../Components/CreditsInfo'; // Using alias for import
import { ClipLoader } from 'react-spinners';
import './Index.css';

export default function Index() {
    const { videos: initialVideos, flash, movie_hash, movie_title, credits: initialCredits, user_email } = usePage().props;
    const [credits, setCredits] = useState(initialCredits);
    const [videos, setVideos] = useState(initialVideos);
    const [loading, setLoading] = useState(false);

    const { data: uploadData, setData: setUploadData, post: uploadPost, processing: uploading } = useForm({
        video_file: null,
        movie_hash: movie_hash
    });

    useEffect(() => {
        console.log('flash flash:', flash);
        if (flash?.success) {
            toast.success(flash.success);
        }
        if (flash?.error) {
            toast.error(flash.error);
        }
        if (flash?.message) {
            toast.info(flash.message);
        }
        if (flash?.status) {
            toast.info(flash.status);
        }
    }, [flash]);

    useEffect(() => {
        fetchCredits();
    }, []);

    const fetchCredits = async () => {
        try {
            const response = await axios.get(`/credits/${movie_hash}`);
            setCredits(response.data.credits);
        } catch (error) {
            console.error('Error fetching credits:', error);
            toast.error('Error fetching credits.');
        }
    };

    const handleDelete = async (e, video) => {
        e.preventDefault();
        const confirmed = confirm('Are you sure you want to delete this video?');
        if (confirmed) {
            try {
                const csrfToken = document.querySelector('meta[name="csrf-token"]').getAttribute('content');
                const response = await axios.post(`/videos/${video.uuid}`, {
                    _method: 'DELETE',
                    _token: csrfToken,
                });
                if (response.data.success) {
                    toast.success('Video deleted successfully.');
                    setVideos(videos.filter(v => v.id !== video.id));
                } else {
                    throw new Error('Failed to delete video.');
                }
            } catch (error) {
                console.error('Error deleting video:', error);
                toast.error('Error deleting video.');
            }
        }
    };

    const handleEditClick = (url) => {
        setLoading(true);
        router.visit(url, {
            onFinish: () => setLoading(false),
        });
    };

    const handleFileUpload = (e) => {
        const file = e.target.files[0];
        if (!file) return;
        // We need to construct FormData manually for file uploads with Inertia if not using the automatic binding logic strictly
        const formData = new FormData();
        formData.append('video_file', file);
        formData.append('movie_hash', movie_hash);
        router.post('/videos/upload', formData, {
            forceFormData: true,
            onStart: () => setLoading(true),
            onFinish: () => setLoading(false),
            onSuccess: () => toast.success('Video uploaded successfully'),
        });
    };

    return (
        <div className="container">
            <ToastContainer />
            <CreditsInfo
                movie_title={movie_title}
                movie_hash={movie_hash}
                initialCredits={credits}
                user_email={user_email}
            />
            <br />
            <Link href={`/movies/${movie_hash}/videos/create`} className="btn btn-primary mb-3">+ Create your own take</Link>
            
            {/* Upload Video Section */}
            <input 
                type="file" 
                id="video-upload" 
                style={{ display: 'none' }} 
                accept="video/mp4,video/quicktime,video/x-msvideo,video/webm"
                onChange={handleFileUpload}
            />
            <button 
                className="btn btn-secondary mb-3 ms-2" 
                onClick={() => document.getElementById('video-upload').click()}
                disabled={loading || uploading}
            >
                {uploading ? 'Uploading...' : 'Upload Video'}
            </button>

            <h1>Videos for {movie_title}</h1>

            <table className="table">
                <thead>
                    <tr>
                        <th>Date Created</th>
                        <th>Title</th>
                        <th>Status</th>
                        <th>Primary</th>
                        <th>Actions</th>
                    </tr>
                </thead>
                <tbody>
                    {videos.map(video => (
                        <tr key={video.id}>
                            <td>{formatDistanceToNow(parseISO(video.created_at), { addSuffix: true })}</td>
                            <td>{video.title}</td>
                            <td>{video.status}</td>
                            <td>{video.primary ? 'Primary' : ''}</td>
                            <td>
                                {/* Edit Button - Hide for uploads */}
                                {video.status !== 'Image Prompts In Progress' && video.status !== 'Video uploaded' && (
                                    <button
                                        className="btn btn-secondary me-2"
                                        onClick={() => handleEditClick(`/videos/${video.uuid}`)}
                                    >
                                        Edit
                                    </button>
                                )}
                                {/* Download Button - Show for videos with a built video */}
                                {video.created_video_id && (
                                    <a
                                        href={`/videos/${video.uuid}/download`}
                                        className="btn btn-success me-2"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    >
                                        Download
                                    </a>
                                )}
                                {/* Set as Primary - Allow for created or uploaded */}
                                {(video.status === 'Video created' || video.status === 'Video uploaded') && !video.primary && (
                                    <Link href={`/videos/${video.uuid}/primary`} className="btn btn-secondary me-2" method="post" as="button">
                                        Set as Primary
                                    </Link>
                                )}
                                <button
                                    className="btn btn-danger"
                                    onClick={(e) => handleDelete(e, video)}
                                >
                                    Delete
                                </button>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
            {loading && (
                <div className="loading-indicator">
                    <ClipLoader size={50} color={"#123abc"} loading={loading} />
                </div>
            )}
        </div>
    );
}
